import type { ConnectOptions } from "/@/shared/motion-menu-system/MenuTypes";
import type { OptionDefinition } from "./types.ts";
import { allOptionDefinitions } from "./definitions/index.ts";

/** Build default options object from definitions */
function buildDefaultsFromDefinitions(): ConnectOptions {
  const defaults: Record<string, unknown> = {};
  for (const def of allOptionDefinitions) {
    defaults[def.key] = def.default;
  }
  return defaults as ConnectOptions;
}

export const defaultOptions = buildDefaultsFromDefinitions();

export function mergeWithDefaults(
  loadedOptions: Partial<ConnectOptions> | undefined,
): ConnectOptions {
  if (!loadedOptions) {
    return { ...defaultOptions };
  }

  const merged = { ...defaultOptions } as Record<string, unknown>;
  for (const key in loadedOptions) {
    const value = (loadedOptions as Record<string, unknown>)[key];
    if (value !== undefined) {
      merged[key] = value;
    }
  }
  return merged as ConnectOptions;
}

/** Check if value equals default (handles objects via JSON comparison) */
function isDefaultValue(
  key: string,
  value: unknown,
  defaults: Record<string, unknown>,
): boolean {
  const defValue = defaults[key];
  if (value === defValue) return true;
  if (typeof value === "object" && typeof defValue === "object") {
    return JSON.stringify(value) === JSON.stringify(defValue);
  }
  return false;
}

export function removeDefaults(
  options: Partial<ConnectOptions> | undefined,
): Partial<ConnectOptions> | undefined {
  if (!options) {
    return options;
  }

  const reduced: Partial<ConnectOptions> = { ...options };
  const defaults = defaultOptions as Record<string, unknown>;

  for (const key in options) {
    const value = (options as Record<string, unknown>)[key];
    if (isDefaultValue(key, value, defaults)) {
      delete (reduced as Record<string, unknown>)[key];
    }
  }
  return Object.keys(reduced).length > 0 ? reduced : undefined;
}

/** Get option definition by key */
export function getOptionDefinition(key: string): OptionDefinition | undefined {
  return allOptionDefinitions.find((d) => d.key === key);
}

export { allOptionDefinitions };
